import React, { useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'

import { formatDate } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { useTracking } from '../../../apps/professional-app/fa/common/tracking/hooks'
import { TrackEvent } from '../../../apps/professional-app/fa/common/tracking'

import { selectDocument, selectDocumentFileInfo, selectDocumentFileInfoIsLoading, selectDocumentLoading } from '../../../store/processDocument/document'
import { selectDocumentBatchHasMultipleDocuments } from '../../../store/processDocument/documentBatch'
import { displayDocumentSidebar } from '../../../store/processDocument/documentSidebar'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

import { type User } from '../../../models/User'

import { Button } from '../../../components/Button/Button'
import { ReactComponent as SyncedTaskStatus } from '../../../assets/images/task-status-synced-stroke.svg'
import { DocumentWorkflowsMenu } from '../../../components/CustomWorkflows/DocumentWorkflowsMenu'

import { DocumentTypeSelectDropdown } from '../DocumentTypeSelectDropdown/DocumentTypeSelectDropdown'
import { DocumentSidebarTitle, DocumentSidebarType } from '../DocumentSidebar/models'
import { DeleteDocumentModal } from '../DocumentDeleteModal'
import { useDocumentLeaveBypass } from '../DocumentLeaveBypassContext'
import { getDocumentSummaryTitle } from '../DocumentSummary/utils'
import { InlineDocumentProcessingStatus } from '../DocumentProcessingStatus/InlineDocumentProcessingStatus'

interface DocumentDetailsProps {
  contact: User
}

export const DocumentDetails: React.FC<DocumentDetailsProps> = ({ contact }) => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()
  const navigate = useNavigate()

  const { bypassOnce } = useDocumentLeaveBypass()

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  const document = useAppSelector(selectDocument)
  const documentLoading = useAppSelector(selectDocumentLoading)
  const fileInfo = useAppSelector(selectDocumentFileInfo)
  const fileInfoLoading = useAppSelector(selectDocumentFileInfoIsLoading)
  const hasMultipleDocuments = useAppSelector(selectDocumentBatchHasMultipleDocuments)

  if (documentLoading || fileInfoLoading) {
    return <div className='skeleton w-100 document-title-skeleton' />
  }

  if (!document) {
    return null
  }

  const documentIsProcessing = !document.summaryProcessing.finishedAt

  const _handleOpenExternalUpdates = () => {
    dispatch(displayDocumentSidebar({ type: DocumentSidebarType.EXTERNAL_UPDATES, title: DocumentSidebarTitle.EXTERNAL_UPDATES, icon: 'bi-arrow-repeat' }))
  }

  const handleOpenExternalUpdates = tracked(_handleOpenExternalUpdates, {
    eventName: TrackEvent.EXTERNAL_UPDATES_OPENED,
    eventDetails: { documentId: document.id },
  })

  const handleShowDeleteModal = () => {
    setShowDeleteModal(true)
  }

  const handleHideDeleteModal = () => {
    setShowDeleteModal(false)
  }

  const handleDeleteSuccess = () => {
    bypassOnce()
    navigate(hasMultipleDocuments ? `/document-batches/${document.documentBatchId}` : '/documents/upload')
  }

  return (
    <>
      {showDeleteModal ? (
        <DeleteDocumentModal show={showDeleteModal} onHide={handleHideDeleteModal} onSuccess={handleDeleteSuccess} document={document} />
      ) : null}
      <Row className='d-flex align-items-center justify-content-between flex-nowrap'>
        <Col className='d-flex flex-column gap-1 mw-0'>
          <h2>{getDocumentSummaryTitle(document, contact)}</h2>
          {!documentIsProcessing ? (
            <div className='d-flex align-items-center zocks-gap-0.75 paragraph-medium zocks-text-gray-700 mw-0'>
              <InlineDocumentProcessingStatus document={document} />
              <div className='document-summary-header-divider flex-shrink-0 zocks-bg-color-gray-700 rounded-circle' />
              <span className='text-nowrap flex-shrink-0'>{formatDate(document.createdAt)}</span>
              <div className='document-summary-header-divider flex-shrink-0 zocks-bg-color-gray-700 rounded-circle' />
              {fileInfo ? (
                <span className='text-truncate mw-0'>{fileInfo.fileName}</span>
              ) : (
                <div className='d-flex align-items-center gap-1 flex-shrink-0'>
                  <i className='bi bi-file-earmark-x fs-6' />
                  <span>File deleted</span>
                </div>
              )}
            </div>
          ) : null}
        </Col>
        <Col className='d-flex align-items-center gap-3' xs='auto'>
          <DocumentWorkflowsMenu documentId={document.id} />
          <DocumentTypeSelectDropdown disabled={!fileInfo || documentIsProcessing} />
          <Button variant='header' className='zocks-p-0.625' onClick={handleOpenExternalUpdates}>
            <SyncedTaskStatus width={18} height={18} stroke='#000' />
          </Button>
          <div className='document-summary-header-icon-divider flex-shrink-0 align-self-stretch zocks-bg-color-gray-300' />
          <Button variant='header' className='zocks-p-0.625' onClick={handleShowDeleteModal} disabled={documentIsProcessing}>
            <i className='bi bi-trash fs-6' />
          </Button>
        </Col>
      </Row>
    </>
  )
}
